import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ColumnOrderState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  OnChangeFn,
  Table as TanstackTable,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTableLoadMoreCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { DefaultTableEmptyState } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { useColumnSizing } from '@/hooks/use-column-sizing';
import { TableIds } from '@/lib/constants/table-ids';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { getApprovalsTableColumns } from '../../constants/approvals-table-columns';
import { isEmptyEntriesRow, isLoadMoreRow } from '../../helpers/approval-load-more-row.helper';
import {
  getApprovalEntryAncestorRowIds,
  getApprovalEntryRowId,
  getApprovalRowId,
  getApprovalSubRows,
  hasApprovalChildren,
  isApprovalTimeEntry,
  isApprovalTimeOffEntry,
} from '../../helpers/approval-row-model.helper';
import { getApprovalSelectionCounts } from '../../helpers/approval-selection-counts.helper';
import { hasAnyInvalidApprovedInput } from '../../helpers/invalid-approved-inputs.helper';
import { mergeEditedEntryValues } from '../../helpers/merge-edited-entry.helper';
import { deriveApprovalSelection } from '../../helpers/table-selection.helpers';
import { useApprovalSectionObserver } from '../../hooks/use-approval-section-observer';
import { useApprovalSentinelRows } from '../../hooks/use-approval-sentinel-rows';
import { useLazyApprovalData } from '../../hooks/use-lazy-approval-data';
import { useApprovalsStore } from '../../store/approvals.store';
import { ApprovalsTableMeta, ApprovedHoursField } from '../../types/approvals-table.types';
import { GetApprovalFilterRequest } from '../../types/dto/worklogs-approval.dto';
import {
  ApprovalProjectRow,
  ApprovalSelection,
  ApprovalTableRow,
  ApprovalTimeEntry,
  TimeEntryStatus,
} from '../../types/time-tracking-approvals';
import {
  EmptyDescriptionInfoModal,
  EmptyDescriptionScope,
} from '../modals/empty-description-info-modal';

interface TimeEntriesTableProps {
  data: ApprovalProjectRow[];
  filter: GetApprovalFilterRequest;
  isLoading?: boolean;
  /** True while the top-level projects list has more pages to load. */
  hasMoreProjects?: boolean;
  /** True while the next page of top-level projects is being fetched. */
  isFetchingMoreProjects?: boolean;
  /** Requests the next page of top-level projects (driven by the scroll observer). */
  onLoadMoreProjects?: () => void;
  /**
   * Optional external ref to the table's vertical scroll container — the page uses it to
   * drive the summary-cards collapse. The table works identically without it.
   */
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  /** Controlled column visibility (persisted by the page). */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  /** Controlled column order (persisted by the page) — drives drag-to-reorder. */
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
  /**
   * Called once with the (identity-stable) table instance, so the page can render
   * table-bound chrome (the columns-visibility dropdown) up in its toolbar row.
   */
  onTableReady?: (table: TanstackTable<ApprovalProjectRow>) => void;
  onApprove?: (entry: ApprovalTimeEntry) => void;
  onRevert?: (entry: ApprovalTimeEntry) => void;
  onBulkApprove?: (selection: ApprovalSelection) => void;
  onBulkRevert?: (selection: ApprovalSelection) => void;
}

export function TimeEntriesTable({
  data,
  filter,
  isLoading,
  hasMoreProjects = false,
  isFetchingMoreProjects = false,
  onLoadMoreProjects,
  scrollContainerRef: externalScrollContainerRef,
  columnVisibility = {},
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  onTableReady,
  onApprove,
  onRevert,
  onBulkApprove,
  onBulkRevert,
}: TimeEntriesTableProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [emptyDescriptionScope, setEmptyDescriptionScope] = useState<EmptyDescriptionScope | null>(
    null
  );
  const {
    rowSelection,
    exclusions,
    setRowSelection,
    editedEntryValues,
    editedEntrySnapshots,
    setApprovedForInvoiceHours,
    setApprovedForBillHours,
    setApprovalNotes,
    invalidApprovedInputs,
    markApprovedInputInvalid,
    clearApprovedInputInvalid,
  } = useApprovalsStore();

  /**
   * Editing a row means "include it in the approval", so the edited row gets selected.
   *
   * But NOT when a selected project/member already covers it: only one selection level can
   * be active at a time, so adding the entry's own row id would DROP that ancestor and
   * silently shrink a whole-member bulk approve down to the single edited row. The
   * aggregate already includes the row, and its edited values ride along in the request
   * (see `mapApprovalSelectionToRequest`).
   *
   * A covered row that the user explicitly UNCHECKED (an exclusion) is left excluded — the
   * edit is recorded and stays visible as pending; re-checking the row includes it again.
   */
  const selectEditedEntry = useCallback(
    (entry: ApprovalTimeEntry) => {
      const { rowSelection: selection } = useApprovalsStore.getState();
      const rowId = getApprovalEntryRowId(entry.id);
      if (selection[rowId]) return;
      if (getApprovalEntryAncestorRowIds(entry).some(ancestorRowId => selection[ancestorRowId])) {
        return;
      }
      setRowSelection(prev => ({ ...prev, [rowId]: true }));
    },
    [setRowSelection]
  );

  const handleApprovedForInvoiceHoursChange = useCallback(
    (entry: ApprovalTimeEntry, hours: number) => {
      setApprovedForInvoiceHours(entry, hours);
      selectEditedEntry(entry);
    },
    [setApprovedForInvoiceHours, selectEditedEntry]
  );

  const handleApprovedForBillHoursChange = useCallback(
    (entry: ApprovalTimeEntry, hours: number) => {
      setApprovedForBillHours(entry, hours);
      selectEditedEntry(entry);
    },
    [setApprovedForBillHours, selectEditedEntry]
  );

  // An unparseable approved input never commits, so approving while one exists would save the
  // row's OLD value behind the user's back. Block the bulk action instead.
  const hasInvalidApprovedInput = hasAnyInvalidApprovedInput(invalidApprovedInputs);

  const handleApprovedHoursValidityChange = useCallback(
    (entry: ApprovalTimeEntry, field: ApprovedHoursField, rawValue: string | null) => {
      if (rawValue === null) {
        clearApprovedInputInvalid(entry.id, field);
        return;
      }
      // The ancestor ids ride along so the member and project rows can warn about this entry
      // even after it has been collapsed out of the loaded tree.
      markApprovedInputInvalid({
        entryId: entry.id,
        projectId: entry.projectId,
        memberId: entry.memberId,
        field,
        rawValue,
      });
    },
    [markApprovedInputInvalid, clearApprovedInputInvalid]
  );

  const handleApprovalNotesChange = useCallback(
    (entry: ApprovalTimeEntry, notes: string | null) => {
      setApprovalNotes(entry, notes);
      selectEditedEntry(entry);
    },
    [setApprovalNotes, selectEditedEntry]
  );

  const {
    columnSizing,
    columnSizingInfo,
    onColumnSizingChange,
    onColumnSizingInfoChange,
    saveColumnSizing,
  } = useColumnSizing(TableIds.TIME_TRACKING_APPROVALS);

  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(OpsiumPolicy.WorklogsApprove);
  const canViewBillingRates = hasPermission(OpsiumPolicy.ProjectsBillingManage);

  // Collapse the tree on filter change (sections reload under the new filter), but KEEP
  // the selection, exclusions, and edits: they're keyed by stable row/entry ids and must
  // survive filter round-trips — rows hidden by the current filter simply don't resolve
  // into the action payload until they're visible again.
  //
  // Keyed on the SERIALIZED filter, never the object identity: a caller that rebuilds the
  // filter object per render (e.g. a `?? { … }` fallback) would otherwise collapse the tree —
  // and re-set state — on every render.
  const filterKey = JSON.stringify(filter);
  useEffect(() => {
    setExpanded({});
  }, [filterKey]);

  const internalScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = externalScrollContainerRef ?? internalScrollContainerRef;

  const { enrichedData, loadingRowIds, sectionPagination, sectionLoaders } = useLazyApprovalData({
    projects: data,
    expanded,
    filter,
  });

  // All three levels share ONE scroll observer (roots wired in under a reserved section id).
  const { registerRowRef } = useApprovalSectionObserver({
    enrichedData,
    expanded,
    sectionPagination,
    scrollContainerRef,
    hasMoreProjects,
    isFetchingMoreProjects,
    onLoadMoreProjects,
  });

  // The tree the table model renders: `enrichedData` plus per-section "loading more" and
  // "no entries" sentinel rows. Reference-stable when nothing is fetching (see the hook).
  const tableData = useApprovalSentinelRows({
    enrichedData,
    expanded,
    sectionPagination,
    loadingRowIds,
    isFetchingMoreProjects,
  });

  // Reads the live store (getState), not a render snapshot: an edit committed by the same
  // click's blur is in the store synchronously, before this component re-renders.
  const mergeEditedValues = useCallback(
    (entry: ApprovalTimeEntry): ApprovalTimeEntry =>
      mergeEditedEntryValues(entry, useApprovalsStore.getState().editedEntryValues[entry.id]),
    []
  );

  const columns = useMemo(
    () =>
      getApprovalsTableColumns({
        isApproveActionsVisible: canApprove,
        isBillingRatesVisible: canViewBillingRates,
        onApprove: entry => onApprove?.(mergeEditedValues(entry)),
        onRevert: entry => onRevert?.(mergeEditedValues(entry)),
        onShowEmptyDescriptionInfo: scope => setEmptyDescriptionScope(scope),
      }),
    [onApprove, onRevert, canApprove, canViewBillingRates, mergeEditedValues]
  );

  // Stable `meta` identity so memoized cells don't re-render on unrelated state changes
  // (e.g. toggling a checkbox). Only changes when its actual inputs change.
  const tableMeta = useMemo<ApprovalsTableMeta>(
    () => ({
      loadingRowIds,
      onApprovedForInvoiceHoursChange: handleApprovedForInvoiceHoursChange,
      onApprovedForBillHoursChange: handleApprovedForBillHoursChange,
      onApprovalNotesChange: handleApprovalNotesChange,
      onApprovedHoursValidityChange: handleApprovedHoursValidityChange,
    }),
    [
      loadingRowIds,
      handleApprovedForInvoiceHoursChange,
      handleApprovedForBillHoursChange,
      handleApprovalNotesChange,
      handleApprovedHoursValidityChange,
    ]
  );

  const table = useReactTable<ApprovalProjectRow>({
    data: tableData,
    columns,
    state: {
      expanded,
      rowSelection,
      columnSizing,
      columnSizingInfo,
      columnVisibility,
      // An empty array is valid ("natural order"); undefined leaves the state uncontrolled.
      columnOrder: columnOrder ?? [],
    },
    initialState: {
      // Freeze the selection checkbox and the tree column while scrolling horizontally.
      columnPinning: { left: ['select', 'projectMember'] },
    },
    defaultColumn: {
      minSize: 100,
      maxSize: 600,
    },
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange,
    onColumnSizingInfoChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    // Synthetic load-more rows and informational time-off rows are never selectable.
    enableRowSelection: row =>
      !isLoadMoreRow(row.original as ApprovalTableRow) &&
      !isApprovalTimeOffEntry(row.original as ApprovalTableRow),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowCanExpand: row => hasApprovalChildren(row.original as ApprovalTableRow),
    getRowId: row => getApprovalRowId(row as ApprovalTableRow),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row =>
      getApprovalSubRows(row as ApprovalTableRow) as ApprovalProjectRow[] | undefined,
    meta: tableMeta,
  });

  // Surfaces the (identity-stable) table instance to the page so it can render the
  // columns-visibility dropdown in its own toolbar row, above this component.
  useEffect(() => {
    onTableReady?.(table);
  }, [onTableReady, table]);

  // Clicking anywhere on an aggregate row is a second affordance for its chevron; leaf rows
  // (worklogs, time-off days, sentinels) have no children and must stay inert so a click on
  // their editable cells never toggles anything.
  const handleRowClick = useCallback((rowData: ApprovalProjectRow) => {
    const row = rowData as ApprovalTableRow;
    if (!hasApprovalChildren(row)) return;

    const rowId = getApprovalRowId(row);
    setExpanded(prev => {
      // ExpandedState is `true | Record<string, boolean>` — `true` means "everything expanded".
      const current = typeof prev === 'object' ? prev : {};
      return { ...current, [rowId]: !current[rowId] };
    });
  }, []);

  // Selected leaf time-entry rows, with pending edits merged in. `flatRows` spans all depths.
  const selectedFlatRows = table.getSelectedRowModel().flatRows;
  const mapSelectedTimeEntries = useCallback(
    (): ApprovalTimeEntry[] =>
      selectedFlatRows
        .filter(row => isApprovalTimeEntry(row.original) && !isLoadMoreRow(row.original))
        .map(row => mergeEditedValues(row.original as unknown as ApprovalTimeEntry)),
    [selectedFlatRows, mergeEditedValues]
  );

  // For the footer counts/labels only. `editedEntryValues` is a dependency so the memo
  // recomputes as edits land (mergeEditedValues reads the store imperatively, not a closure).
  const selectedTimeEntries = useMemo(mapSelectedTimeEntries, [
    mapSelectedTimeEntries,
    editedEntryValues,
  ]);

  // The active selection across the three levels (projects / members / worklogs).
  // Project/member rows resolve directly from the selection store + loaded tree — their
  // children do NOT need to be expanded or loaded. `editedEntryValues` feeds the pending
  // inline edits an aggregate selection covers (they change its "hours to approve").
  const selection = useMemo(
    () =>
      deriveApprovalSelection({
        rowSelection,
        exclusions,
        projects: enrichedData,
        selectedEntries: selectedTimeEntries,
        editedEntryValues,
        editedEntrySnapshots,
      }),
    [
      rowSelection,
      exclusions,
      enrichedData,
      selectedTimeEntries,
      editedEntryValues,
      editedEntrySnapshots,
    ]
  );

  // Footer button counts. Approvable = still-pending entries, unapprovable = already-approved.
  // Worklog level reads each entry's status; project/member level uses the backend
  // pending/approved worklog aggregates (children may not be loaded). Each button disables
  // when its side is empty (see `disabled={…Count === 0}` below).
  const { approveCount, unapproveCount } = useMemo(
    () => getApprovalSelectionCounts(selection),
    [selection]
  );

  // Bulk actions re-derive at click time so a just-blurred edit (same tick as the click) is
  // included — the memoized `selection` above hasn't re-rendered with it yet.
  const deriveSelectionForAction = useCallback(
    (statusFilter: TimeEntryStatus): ApprovalSelection => {
      const store = useApprovalsStore.getState();
      return deriveApprovalSelection({
        rowSelection: store.rowSelection,
        exclusions: store.exclusions,
        projects: enrichedData,
        selectedEntries: mapSelectedTimeEntries().filter(e => e.status === statusFilter),
        editedEntryValues: store.editedEntryValues,
        editedEntrySnapshots: store.editedEntrySnapshots,
      });
    },
    [enrichedData, mapSelectedTimeEntries]
  );

  const handleBulkApprove = useCallback(() => {
    onBulkApprove?.(deriveSelectionForAction(TimeEntryStatus.SUBMITTED));
  }, [deriveSelectionForAction, onBulkApprove]);

  const handleBulkUnapprove = useCallback(() => {
    onBulkRevert?.(deriveSelectionForAction(TimeEntryStatus.APPROVED));
  }, [deriveSelectionForAction, onBulkRevert]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasSelectedRows = Object.keys(rowSelection).length > 0;
  const isEmpty = enrichedData.length === 0;

  // "Loading more" spinners are rendered inline per-section as synthetic rows (see
  // `useApprovalSentinelRows`), so the bottom row only carries the empty state.
  // Visible leaf columns only — a hidden column must not widen the empty state's colSpan.
  const colSpan = table.getVisibleLeafColumns().length;
  const lastRowChildren = isEmpty ? <DefaultTableEmptyState colSpan={colSpan} /> : undefined;

  return (
    <>
      {sectionLoaders}
      <DataTable
        table={table}
        variant="default"
        onRowClick={handleRowClick}
        stickyHeader
        enableVirtualization
        scrollContainerRef={scrollContainerRef}
        registerRowRef={registerRowRef}
        tableId={TableIds.TIME_TRACKING_APPROVALS}
        onResizeEnd={saveColumnSizing}
        lastRowChildren={lastRowChildren}
        renderFullWidthRow={rowData => {
          const row = rowData as ApprovalTableRow;
          if (isEmptyEntriesRow(row)) {
            return (
              <div className="flex h-6 w-full items-center justify-center text-sm text-muted-foreground">
                {t('approvals.noTimeEntries')}
              </div>
            );
          }
          if (isLoadMoreRow(row)) {
            return <DataTableLoadMoreCell label={t('approvals.loadingMore')} />;
          }
          return undefined;
        }}
      />
      {hasSelectedRows && canApprove && (
        <div className="absolute bottom-0 left-0 z-50 flex w-full items-center justify-end gap-2 border-t border-border bg-surface p-4 shadow-[0_-1px_4px_0_hsl(var(--border))]">
          <Button onClick={handleBulkUnapprove} disabled={unapproveCount === 0}>
            {t('approvals.actions.unapproveSelected', { count: unapproveCount })}
          </Button>

          <SimpleTooltip
            content={
              hasInvalidApprovedInput ? t('approvals.actions.approveBlockedByInvalidInput') : null
            }
          >
            {/* A disabled button emits no pointer events — the span is what the tooltip hangs
                off, so the user can find out why approving is blocked. */}
            <span className="inline-flex">
              <Button
                onClick={handleBulkApprove}
                disabled={approveCount === 0 || hasInvalidApprovedInput}
              >
                {t('approvals.actions.approveSelected', { count: approveCount })}
              </Button>
            </span>
          </SimpleTooltip>
        </div>
      )}
      <EmptyDescriptionInfoModal
        open={emptyDescriptionScope !== null}
        onOpen={() => {}}
        onClose={() => setEmptyDescriptionScope(null)}
        scope={emptyDescriptionScope ?? 'entry'}
      />
    </>
  );
}
